import React, { ReactElement } from 'react'
import { MessageBotCommand, MessageBotMenu, MessageFile } from './MessageInner'
import Avatar from '../components/Avatar'
import MessageStatus from '../components/MessageStatus'
import styled from 'styled-components'
import MessageFormatter from './MessageFormatter'
import { Services } from '../utils'
import { MessageImageGroup } from './MessageImageGroup'
import { messageStyles } from '../styles/messages'

const TimeDiv = styled.div`
  font-size: 10px;
  white-space: nowrap;
  color: ${props => props.theme.color.main.background};
  opacity: 0;
  margin: 0 10px 0 10px;
  transition: opacity 0.2s 5s;
  user-select: none;
  pointer-events: none;
`

const MessageTime = (params: { message: IMessage }) => {
  const time = Services.dateFormatter.format(params.message.created, '%h:%i')
  return <TimeDiv data-type="time">{time}</TimeDiv>
}

const ChatAvatarHolderDiv = styled.div`
  position: absolute;
  bottom: 3px;
  left: -45px;
`
const ChatMessageDiv = styled.div<{ isLast?: boolean }>`
  position: relative;
  display: flex;
  align-self: flex-start;
  align-items: center;
  width: 88%;
  max-width: 88%;

  &[data-mine="true"] {
    justify-content: flex-end;
    align-self: flex-end;
    color: ${props => props.theme.color.main.messageMyTextWrapper};
  }

  &[data-mine="false"] {
    flex-direction: row-reverse;
    justify-content: flex-end;
    margin-left: 50px;
  }

  &[data-mine="true"] {
    &[data-position="last"],
    &[data-position="standalone"] {
      &[data-type="text"] {
        &::after {
          ${messageStyles.messageCloudTaleRight}
          background-color: ${props => props.theme.color.main.messageMy};
        }
      }
    }
  }

  &[data-mine="false"] {
    &[data-position="last"],
    &[data-position="standalone"] {
      &[data-type="text"],
      &[data-type="botMenu"] {
        &::after {
          ${messageStyles.messageCloudTaleLeft}
          background-color: ${props => props.theme.color.main.messagePartner};
        }
      }
    }
  }

  & > div[data-status="plus"] {
    background-color: #4caf50;
    color: white;

    :before {
      border-color: #4caf50 !important;
    }
  }

  & > div[data-status="minus"] {
    background-color: #ce3f44;
    color: white;

    :before {
      border-color: #ce3f44 !important;
    }
  }

  &[data-mine="true"] {
    &[data-position="middle"] {
      & div[data-type="message"] {
        border-radius: ${props => {
          const regularRadius = props.theme.borderRadius.chatMessage.regular
          const smallRadius = props.theme.borderRadius.chatMessage.small
          return `${regularRadius} ${smallRadius} ${smallRadius} ${regularRadius}`
        }};
      }
    }

    &[data-position="first"] {
      & div[data-type="message"] {
        border-radius: ${props => {
          const regularRadius = props.theme.borderRadius.chatMessage.regular
          const smallRadius = props.theme.borderRadius.chatMessage.small
          return `${regularRadius} ${regularRadius} ${smallRadius} ${regularRadius}`
        }};
      }
    }

    &[data-position="last"] {
      & div[data-type="message"] {
        border-radius: ${props => {
          const regularRadius = props.theme.borderRadius.chatMessage.regular
          const smallRadius = props.theme.borderRadius.chatMessage.small
          return `${regularRadius} ${smallRadius} ${regularRadius} ${regularRadius}`
        }};
      }
    }
  }

  &[data-mine="false"] {
    &[data-position="middle"] {
      & div[data-type="message"] {
        border-radius: ${props => {
          const regularRadius = props.theme.borderRadius.chatMessage.regular
          const smallRadius = props.theme.borderRadius.chatMessage.small
          return `${smallRadius} ${regularRadius} ${regularRadius} ${regularRadius}`
        }};
      }
    }

    &[data-position="first"] {
      & div[data-type="message"] {
        border-radius: ${props => {
          const regularRadius = props.theme.borderRadius.chatMessage.regular
          const smallRadius = props.theme.borderRadius.chatMessage.small
          return `${regularRadius} ${regularRadius} ${regularRadius} ${smallRadius}`
        }};
      }
    }

    &[data-position="last"] {
      & div[data-type="message"] {
        border-radius: ${props => {
          const regularRadius = props.theme.borderRadius.chatMessage.regular
          const smallRadius = props.theme.borderRadius.chatMessage.small
          return `${smallRadius} ${regularRadius} ${regularRadius} ${regularRadius}`
        }};
      }
    }
  }

  &[data-position="middle"],
  &[data-position="first"],
  &[data-position="last"] {
    margin-bottom: 4px;
  }

  &[data-position="standalone"],
  &[data-position="last"] {
    margin-bottom: ${props => props.isLast ? 0 : '30px'};
  }

  &:hover > div[data-type="time"] {
    opacity: 1;
    transition: opacity 0.2s;
  }
`
const DateDiv = styled.div`
  background-color: ${props => props.theme.color.main.background};
  padding: 5px 15px 5px 15px;
  font-size: 11px;
  color: ${props => props.theme.color.main.messageTime};
  white-space: nowrap;
  border-radius: 10px;
  margin: 0 0 25px 0;
  text-align: center;
  width: auto;
  align-self: center;
  position: sticky;
  z-index: 1;
  top: 10px;
`

interface Props {
  message: IMessage
  isLast: boolean
}

export const Message = React.memo(({ isLast, message }: Props) => {
  const error = message.status === 'error'
  const thumbPreview = message.status === 'uploading'

  let avatar: ReactElement | null = null
  let inner: ReactElement | null = null
  let date: ReactElement | null = null

  if (message.sys) {
    if (message.type === 'botMenu') inner = <MessageBotMenu data={message.sys} msg={message} />
    if (message.type === 'botCommand') inner = <MessageBotCommand msg={message} />
    if (message.type === 'img') inner = <MessageImageGroup msg={message} />
    if (message.type === 'file') inner = <MessageFile msg={message} />

    if (inner == null && message.sys.title) message.text = message.sys.title
    if (!message.text) message.text = `System msg ${message.sys.method} without title`
  }

  const onMsgClick = (e: React.MouseEvent) => {
    if (e.shiftKey) console.log(message)
  }

  if (message.date) date = <DateDiv>{message.date}</DateDiv>

  if (!message.mine && (message.position === 'last' || message.position === 'standalone'))
    avatar = (
      <ChatAvatarHolderDiv>
        <Avatar initials={message.user_name} avatar={message.user_avatar} />
      </ChatAvatarHolderDiv>
    )

  if (inner == null) inner = <MessageFormatter msg={message} />

  if (message.type === 'chatSystem') return (
    <>
      {date}
      <ChatMessageDiv
        data-position={message.position}
        data-mine={message.mine}
        data-type={message.type}
        onClick={onMsgClick}
      >
        <MessageTime message={message} />
        {inner}{avatar}
      </ChatMessageDiv>
    </>
  )

  return (
    <>
      {date}
      <ChatMessageDiv
        data-position={message.position}
        data-mine={message.mine}
        data-type={message.type}
        onClick={onMsgClick}
        isLast={isLast}
      >
        <MessageTime message={message} />

        {error && <MessageStatus status='unsent' />}
        {thumbPreview && <MessageStatus status='uploading' />}

        {inner}
        {avatar}
      </ChatMessageDiv>
    </>
  )
})
